// src/pages/UploadData.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  ChevronLeft,
  Upload,
  CheckCircle,
  AlertCircle,
  Loader,
  FileText,
  X,
  ChevronDown,
  ChevronUp,
  BookOpen,
} from 'lucide-react';
import Navbar from '../navbar';

const API_BASE_URL = 'https://dpp-api.acespireconsulting.com/api';

// Local auth helper — reads authToken (JWT) not the ory_ opaque token
const getHeaders = () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`,
  };
};

function UploadData() {
  const navigate = useNavigate();

  const [expanded, setExpanded] = useState(false);

  const [productGroups, setProductGroups]   = useState([]);
  const [groupsLoading, setGroupsLoading]   = useState(true);
  const [selectedGroup, setSelectedGroup]   = useState('');

  const [existingInstructions, setExistingInstructions] = useState(null);
  const [instructionsLoading, setInstructionsLoading]   = useState(false);

  const [selectedFile, setSelectedFile] = useState(null);
  const [loading, setLoading]           = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError]               = useState(null);

  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProductGroups = async () => {
      try {
        setGroupsLoading(true);
        const response = await axios.get(`${API_BASE_URL}/product-groups`, {
          headers: getHeaders(),
        });
        setProductGroups(response.data?.data?.groups || []);
      } catch (err) {
        console.error('Failed to fetch product groups:', err);
        setError('Failed to load product groups. Please refresh the page.');
      } finally {
        setGroupsLoading(false);
      }
    };
    fetchProductGroups();
  }, []);

  useEffect(() => {
    if (!selectedGroup) { setExistingInstructions(null); return; }
    const fetchInstructions = async () => {
      try {
        setInstructionsLoading(true);
        setExistingInstructions(null);
        const response = await axios.get(
          `${API_BASE_URL}/disassembly-instructions/${encodeURIComponent(selectedGroup)}`,
          { headers: getHeaders() }
        );
        if (response.data?.success && response.data?.data) {
          setExistingInstructions(response.data.data);
        }
      } catch (err) {
        if (err.response?.status !== 404) console.error('Failed to fetch instructions:', err);
      } finally {
        setInstructionsLoading(false);
      }
    };
    fetchInstructions();
  }, [selectedGroup]);

  const handleToggleExpand = () => {
    setExpanded(prev => !prev);
    setSelectedGroup('');
    setSelectedFile(null);
    setUploadResult(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf') && file.type !== 'application/pdf') {
      setError('Please select a valid PDF file'); setSelectedFile(null); return;
    }
    if (file.size > 20 * 1024 * 1024) {
      setError('File size exceeds 20MB limit'); setSelectedFile(null); return;
    }
    setSelectedFile(file);
    setError(null);
    setUploadResult(null);
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError('Please select a PDF file'); return; }
    if (!selectedGroup) { setError('Please select a product group'); return; }

    setLoading(true);
    setError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('productGroup', selectedGroup);

      const response = await axios.post(
        `${API_BASE_URL}/disassembly-instructions`,
        formData,
        {
          headers: { ...getHeaders(), 'Content-Type': 'multipart/form-data' },
          timeout: 120000,
        }
      );

      if (response.data.success) {
        setUploadResult(response.data);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        if (response.data.data) setExistingInstructions(response.data.data);
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (err) {
      console.error('Upload error:', err);
      setError(
        err.response?.data?.message ||
        err.response?.data?.error ||
        err.message ||
        'Upload failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setSelectedFile(null);
    setError(null);
    setUploadResult(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#EDEDED' }}>
      <Navbar />

      {/* Header */}
      <div className="bg-white px-4 sm:px-6 lg:px-8 py-3 sm:py-4 flex items-center justify-between mx-2 sm:mx-4 mt-2 sm:mt-4 rounded-xl shadow">
        <button
          onClick={() => navigate('/dashboard')}
          className="flex items-center text-blue-600 hover:text-blue-700 transition-colors text-sm sm:text-base"
        >
          <ChevronLeft className="w-4 h-4 sm:w-5 sm:h-5 mr-1" />Back
        </button>
        <h2 className="text-blue-600 font-medium text-sm sm:text-base">Upload Data</h2>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 max-w-xl mx-auto">

        {/* Error Banner */}
        {error && (
          <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-3">
            <AlertCircle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-red-700 flex-1">{error}</p>
            <button onClick={() => setError(null)}>
              <X className="w-4 h-4 text-red-600 hover:text-red-800" />
            </button>
          </div>
        )}

        {/* Success Banner */}
        {uploadResult && (
          <div className="mb-4 bg-green-50 border border-green-200 rounded-lg p-3 flex items-start gap-3">
            <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-xs font-semibold text-green-900">{uploadResult.message}</p>
              <p className="text-xs text-green-700 mt-0.5">
                Instructions uploaded for <span className="font-medium">{selectedGroup}</span>
              </p>
            </div>
            <button onClick={() => setUploadResult(null)}>
              <X className="w-4 h-4 text-green-600 hover:text-green-800" />
            </button>
          </div>
        )}

        {/* File Type Card */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">

          {/* Card Header */}
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <span className="text-sm font-semibold text-gray-700">File Type</span>
            <div className="w-5 h-5 rounded-full bg-blue-600 flex items-center justify-center">
              <FileText className="w-3 h-3 text-white" />
            </div>
          </div>

          {/* Disassembly Instruction Row */}
          <div>
            <button
              onClick={handleToggleExpand}
              className="w-full px-4 py-3.5 flex items-center justify-between hover:bg-gray-50 transition-colors border-b border-gray-100"
            >
              <span className="text-sm text-gray-700">Disassembly Instruction</span>
              {expanded
                ? <ChevronUp className="w-4 h-4 text-gray-400" />
                : <ChevronDown className="w-4 h-4 text-gray-400" />
              }
            </button>

            {/* Expanded Panel */}
            {expanded && (
              <div className="px-4 py-4 bg-gray-50 flex flex-col gap-4">

                {/* Product Group Selection */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-2">
                    Select Product Group
                  </label>
                  {groupsLoading ? (
                    <div className="flex items-center gap-2 py-2">
                      <Loader className="w-4 h-4 animate-spin text-blue-500" />
                      <span className="text-xs text-gray-500">Loading groups...</span>
                    </div>
                  ) : productGroups.length === 0 ? (
                    <p className="text-xs text-gray-400 py-2">No product groups found.</p>
                  ) : (
                    <div className="flex flex-col gap-2">
                      {productGroups.map((group) => (
                        <label
                          key={group.id}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-lg border cursor-pointer transition-colors ${
                            selectedGroup === group.productName
                              ? 'border-blue-500 bg-blue-50'
                              : 'border-gray-200 bg-white hover:bg-gray-50'
                          }`}
                        >
                          <input
                            type="radio"
                            name="productGroup"
                            value={group.productName}
                            checked={selectedGroup === group.productName}
                            onChange={(e) => setSelectedGroup(e.target.value)}
                            className="accent-blue-600"
                          />
                          <span className="text-sm text-gray-700">{group.productName}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>

                {/* Existing Instructions Preview */}
                {selectedGroup && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <BookOpen className="w-4 h-4 text-blue-600" />
                      <span className="text-xs font-medium text-gray-600">Current Instructions</span>
                      <span className="text-xs text-gray-400">— {selectedGroup}</span>
                    </div>
                    {instructionsLoading ? (
                      <div className="flex items-center gap-2 py-2">
                        <Loader className="w-4 h-4 animate-spin text-blue-500" />
                        <span className="text-xs text-gray-500">Loading...</span>
                      </div>
                    ) : existingInstructions ? (
                      <div className="bg-white rounded border border-gray-200 p-3 max-h-28 overflow-y-auto">
                        <p className="text-xs text-gray-700 whitespace-pre-wrap leading-relaxed">
                          {existingInstructions.instructions_text || existingInstructions.instructions}
                        </p>
                        <p className="text-xs text-gray-400 mt-2 pt-2 border-t border-gray-100">
                          Last updated:{' '}
                          {existingInstructions.updated_at
                            ? new Date(existingInstructions.updated_at).toLocaleDateString()
                            : '—'}
                        </p>
                      </div>
                    ) : (
                      <div className="bg-white rounded border border-dashed border-gray-300 p-3 text-center">
                        <p className="text-xs text-gray-400">No instructions uploaded yet for this group.</p>
                      </div>
                    )}
                  </div>
                )}

                {/* File Picker */}
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">PDF File</label>
                  <div className="flex gap-2">
                    <label
                      htmlFor="pdf-upload"
                      className={`px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 cursor-pointer hover:bg-gray-50 text-xs whitespace-nowrap transition-colors ${
                        loading ? 'opacity-50 pointer-events-none' : ''
                      }`}
                    >
                      Choose File
                    </label>
                    <input
                      type="file"
                      id="pdf-upload"
                      accept=".pdf,application/pdf"
                      onChange={handleFileChange}
                      ref={fileInputRef}
                      className="hidden"
                      disabled={loading}
                    />
                    <div className="flex-1 border border-gray-200 rounded px-3 py-2 bg-white text-xs text-gray-500 truncate flex items-center gap-1">
                      {selectedFile
                        ? <><FileText className="w-3 h-3 text-blue-600 flex-shrink-0" />{selectedFile.name}</>
                        : 'No file chosen'
                      }
                    </div>
                  </div>
                  {selectedFile && (
                    <p className="text-xs text-blue-600 mt-1 pl-1">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  )}
                </div>

                {/* Upload / Cancel */}
                <div className="flex gap-2">
                  <button
                    onClick={handleUpload}
                    disabled={!selectedFile || !selectedGroup || loading}
                    className={`flex-1 py-2 rounded text-white text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                      selectedFile && selectedGroup && !loading
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-blue-400 cursor-not-allowed'
                    }`}
                  >
                    {loading
                      ? <><Loader className="w-4 h-4 animate-spin" />Uploading...</>
                      : <><Upload className="w-4 h-4" />Upload</>
                    }
                  </button>
                  <button
                    onClick={handleCancel}
                    disabled={loading}
                    className="flex-1 py-2 rounded bg-gray-300 text-gray-700 hover:bg-gray-400 text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Cancel
                  </button>
                </div>

              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default UploadData;